import { yupResolver } from '@hookform/resolvers/yup'
import AppRegistrationIcon from '@mui/icons-material/AppRegistration'
import { LoadingButton } from '@mui/lab'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import {
  Autocomplete,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import axiosClient from 'utils/axiosClient'
import { useSnackbar } from 'notistack'
import { AxiosClientError, TokenInfo } from 'interface'
import { getAuth, UIAction } from 'app/slices/UI'

interface FormInput {
  name: string
  email: string
  reEmail: string
  password: string
  rePassword: string
  prefectureId: number
  sex: string
  birthday: string
}

export default function Registry() {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useAppDispatch()
  const prefectures = useAppSelector((s) => s.UI.prefectures)
  const [loading, setLoading] = useState(false)
  const [auth, setAuth] = useState<TokenInfo>()
  const schema = yup.object({
    name: yup.string().required().min(4).max(255),
    email: yup.string().required().max(255),
    reEmail: yup
      .string()
      .required()
      .oneOf([yup.ref('email'), null], 'Email must match'),
    password: yup.string().required().min(8).max(50),
    rePassword: yup
      .string()
      .required()
      .oneOf([yup.ref('password'), null], 'Passwords must match'),
    prefectureId: yup.number().required().min(1),
    birthday: yup.string().required(),
  })
  const defaultValues = useMemo(() => {
    return {
      name: '',
      email: '',
      reEmail: '',
      password: '',
      rePassword: '',
      prefectureId: 0,
      sex: 'male',
      birthday: format(new Date(), 'yyyy/MM/01'),
    }
  }, [])
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues,
  })
  useEffect(() => {
    if (auth) dispatch(getAuth())
  }, [auth, dispatch])

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    try {
      setLoading(true)
      const res = await axiosClient.post('/api/users/register', {
        ...data,
        id: data.email,
      })
      setAuth(res.data)
    } catch (error) {
      const { status } = error as AxiosClientError
      const msg = '登録ができませんでした。'

      enqueueSnackbar(msg, {
        variant: 'error',
      })
      status === 409 && dispatch(UIAction.setNotice('アカウントが存在している'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Grid container spacing={2} mt={2}>
      <Grid item xs={12}>
        <Typography
          variant="h4"
          component="h2"
          sx={{ borderBottom: 2 }}
          fontWeight="bold"
        >
          ユーザ情報入力
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Container
          maxWidth="sm"
          disableGutters
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Typography variant="h5" fontWeight="bold">
            プロフィール
          </Typography>
          <Stack my={2}>
            <DatePicker
              label="誕生月"
              toolbarTitle="誕生月"
              views={['year', 'month']}
              minDate={new Date('1950-01-01')}
              disableFuture
              showToolbar={false}
              value={getValues('birthday')}
              onChange={(newDate) => {
                if (newDate) {
                  setValue('birthday', format(newDate, 'yyyy/MM/01'), {
                    shouldValidate: true,
                  })
                }
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      placeholder: '___/__',
                      value: format(
                        new Date(getValues('birthday')),
                        'yyyy年MM月'
                      ),
                      readOnly: true,
                    }}
                  />
                )
              }}
            />
          </Stack>
          <FormControl>
            <FormLabel id="register-sex">性別</FormLabel>
            <RadioGroup
              row
              aria-labelledby="register-sex"
              name="register-sex"
              value={getValues('sex')}
              onChange={(_e, value) => {
                setValue('sex', value, {
                  shouldValidate: true,
                })
              }}
            >
              <FormControlLabel value="male" control={<Radio />} label="男性" />
              <FormControlLabel
                value="female"
                control={<Radio />}
                label="女性"
              />
            </RadioGroup>
          </FormControl>
          <Autocomplete
            disablePortal
            options={[0, ...prefectures.map((v) => v.id)]}
            getOptionLabel={(id) =>
              prefectures.find((f) => f.id === id)?.name || ''
            }
            fullWidth
            value={getValues('prefectureId')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={'都道府県'}
                required
                variant="standard"
                error={'prefectureId' in errors}
              />
            )}
            onChange={(_event, newValue) => {
              newValue &&
                setValue('prefectureId', newValue, {
                  shouldValidate: true,
                })
            }}
          />
          <TextField
            autoFocus
            required
            margin="dense"
            label="お名前（ニックネーム）"
            fullWidth
            variant="standard"
            {...register('name')}
            error={'name' in errors}
            helperText={errors.name?.message}
          />
          <TextField
            required
            margin="dense"
            label="メールアドレス（ログインID）"
            fullWidth
            variant="standard"
            {...register('email')}
            error={'email' in errors}
            helperText={errors.email?.message}
          />
          <TextField
            required
            margin="dense"
            label="メールアドレス（確認）"
            fullWidth
            variant="standard"
            {...register('reEmail')}
            error={'reEmail' in errors}
            helperText={errors.reEmail?.message}
          />
          <TextField
            required
            margin="dense"
            label="パスワード"
            fullWidth
            type="password"
            autoComplete="on"
            variant="standard"
            {...register('password')}
            error={'password' in errors}
            helperText={errors.password?.message}
          />
          <TextField
            required
            margin="dense"
            label="パスワード（確認）"
            fullWidth
            type="password"
            autoComplete="on"
            variant="standard"
            {...register('rePassword')}
            error={'rePassword' in errors}
            helperText={errors.rePassword?.message}
          />
          <Stack justifyContent="center" direction="row">
            <LoadingButton
              disabled={Object.keys(errors).length > 0}
              variant="contained"
              type="submit"
              sx={{
                mt: 4,
                px: 4,
                minWidth: 300,
              }}
              loading={loading}
              color={'primary'}
              loadingPosition="end"
              endIcon={<AppRegistrationIcon />}
            >
              プロフィールを登録する
            </LoadingButton>
          </Stack>
        </Container>
      </Grid>
    </Grid>
  )
}
